import React, { useState } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { Mail, Lock, Eye, EyeOff, ArrowRight } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Login = () => {
  const { language } = useLanguage();
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  // Pre-fill email from ?email= query param (e.g., redirect from register page when profile exists)
  const presetEmail = new URLSearchParams(location.search).get('email') || '';
  const [formData, setFormData] = useState({
    email: presetEmail,
    password: ''
  });

  const successMessage = location.state?.message;

  const content = {
    en: {
      title: "Welcome Back",
      subtitle: "Sign in to your TALITHA KOUMI TUG account",
      email: "Email Address",
      password: "Password",
      submit: "Sign In",
      noAccount: "Don't have an account?",
      register: "Create Profile",
      forgotPassword: "Forgot password?",
      error: "Invalid email or password",
      loading: "Loading..."
    },
    fr: {
      title: "Bon Retour",
      subtitle: "Connectez-vous à votre compte TALITHA KOUMI TUG",
      email: "Adresse courriel",
      password: "Mot de Passe",
      submit: "Se connecter",
      noAccount: "Vous n'avez pas de compte?",
      register: "Créer un Profil",
      forgotPassword: "Mot de passe oublié ?",
      error: "Courriel ou mot de passe invalide",
      loading: "Chargement..."
    }
  };

  const t = content[language] || content.fr;

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const response = await axios.post(`${BACKEND_URL}/api/auth/login`, {
        email: formData.email,
        password: formData.password
      });
      
      login(response.data.user, response.data.token);
      
      if (response.data.user.role === 'admin' || response.data.user.role === 'sub_admin') {
        navigate('/admin');
      } else {
        // Partners go to the user dashboard — it now embeds the Partner Portal as a tab.
        navigate('/dashboard');
      }
    } catch (err) {
      setError(err.response?.data?.detail || t.error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Header */}
      <div className="bg-[#D4AF37] py-4">
        <div className="max-w-7xl mx-auto px-4 flex justify-between items-center">
          <Link to="/" className="flex items-center gap-2">
            <img 
              src="https://customer-assets.emergentagent.com/job_talitha-koumi/artifacts/xsd39lyf_IMG-20260121-WA0012.jpg"
              alt="TALITHA KOUMI TUG"
              className="h-12 w-auto"
            />
          </Link>
          <Link to="/register" className="text-white hover:underline font-medium">
            {t.register}
          </Link>
        </div>
      </div>

      {/* Form */}
      <div className="flex-1 flex items-center justify-center py-12 px-4">
        <div className="bg-white p-8 rounded-lg shadow-xl max-w-md w-full">
          <h1 className="text-2xl font-bold text-[#1D1D1D] mb-2">{t.title}</h1>
          <p className="text-gray-600 mb-6">{t.subtitle}</p>

          {successMessage && (
            <div className="bg-green-50 text-green-600 p-3 rounded-lg mb-4 text-sm">
              {successMessage}
            </div>
          )}

          {error && (
            <div data-testid="login-error-message" className="bg-red-50 text-red-600 p-3 rounded-lg mb-4 text-sm">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} data-testid="login-form" className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t.email}</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  data-testid="login-email-input"
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#D4AF37] focus:border-transparent"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t.password}</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  data-testid="login-password-input"
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  required
                  className="w-full pl-10 pr-12 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#D4AF37] focus:border-transparent"
                />
                <button
                  type="button"
                  data-testid="login-toggle-password"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <div className="flex justify-end">
              <Link to="/forgot-password" data-testid="forgot-password-link" className="text-sm text-[#CD0000] hover:underline font-medium">
                {t.forgotPassword}
              </Link>
            </div>

            <button
              type="submit"
              data-testid="login-submit-button"
              disabled={loading}
              className="w-full bg-[#CD0000] text-white py-3 rounded-lg font-bold flex items-center justify-center gap-2 hover:bg-[#c91560] transition-colors disabled:opacity-50"
            >
              {loading ? t.loading : t.submit} <ArrowRight className="w-5 h-5" />
            </button>
          </form>

          <p className="text-center mt-6 text-gray-600">
            {t.noAccount}{' '}
            <Link to="/register" className="text-[#CD0000] font-medium hover:underline">
              {t.register}
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;
